import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";

import { saveModelFile } from "./use-save-model-file";

export type ReplaceDiagramInput = {
  readonly processModelId: string;
  /** 置き換える先（モデルの `primary_file_name`）。選んだファイルの名前は使わない。 */
  readonly primaryFileName: string;
  readonly xml: string;
  /** 取り込む BPMN の実行プロセス id。 */
  readonly processId: string;
  /** いまの `primary_process_id`（失敗したときに戻す値）。 */
  readonly previousProcessId: string | null;
};

/**
 * 主 BPMN の中身を、利用者が持ち込んだファイルで置き換える（「書き出し」の逆）。
 *
 * 書き出した図を外部のツールで直して戻す使い方を想定している。選んだファイルの
 * 名前（`xxx (1).bpmn` など）で保存すると別ファイルが増えるだけで図は変わらない
 * ため、保存先は常に主 BPMN のファイル名にする。
 *
 * 実行プロセス id が変わっていても、モデルの設定は別に送らない。backend は主 BPMN を
 * 保存するとき、`primary_process_id` を新しい図の実行プロセスに合わせて更新する
 * （`SpecFileService.update_file`）。
 *
 * ただし backend は、図の検証（プロセス id が他のモデルと重なっていないか）より先に
 * `process_model.json` を書き換える。保存が失敗しても設定だけが新しい id になり、
 * 起票できないモデルが残るので、失敗したら元の値に戻す。
 *
 * 楽観ロック（file_contents_hash）は付けない。図の編集（use-save-model-file）は
 * 読み込んだ内容を直して返すので他人の更新を踏みうるが、こちらは置き換えることを
 * 利用者が確認欄で承諾した操作のため。
 */
export async function replaceDiagram(
  client: ApiClient,
  input: ReplaceDiagramInput,
): Promise<void> {
  try {
    await saveModelFile(client, {
      processModelId: input.processModelId,
      fileName: input.primaryFileName,
      contents: input.xml,
      contentsHash: null,
    });
  } catch (cause) {
    const mayHaveChanged =
      input.previousProcessId !== null &&
      input.previousProcessId !== input.processId;
    if (!mayHaveChanged) throw cause;

    try {
      // 戻すのは primary_process_id だけ（他の項目まで送ると古い値で上書きしうる）
      await client.put(
        `/process-models/${toModifiedProcessModelId(input.processModelId)}`,
        { primary_process_id: input.previousProcessId },
      );
    } catch {
      throw new DiagramRestoreError(cause);
    }
    throw cause;
  }
}

/**
 * 図の保存に失敗し、書き換わった可能性のある実行プロセスの設定も戻せなかった。
 * cause は図の保存が失敗した理由。
 */
export class DiagramRestoreError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super("could not restore primary_process_id after a failed diagram save");
    this.name = "DiagramRestoreError";
    this.cause = cause;
  }
}

export function useReplaceDiagram(): UseMutationResult<
  void,
  Error,
  ReplaceDiagramInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: ReplaceDiagramInput) => replaceDiagram(client, input),
    onSuccess: () => {
      notifySuccess(t("toast.diagramReplaced"));
    },
    // 図と実行プロセス（primary_process_id）が変わる。失敗しても設定が変わって
    // いる可能性があるので、成否によらず読み直す
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: qk.processModels._root(),
      });
    },
  });
}
